/* eslint-disable @typescript-eslint/no-unsafe-return */
import * as glassEasel from 'glass-easel';
import { Behavior, ComponentType, TraitBehavior } from '../behavior';
export class BaseBehaviorBuilder {
    constructor() {
        this._$parents = [];
    }
    /** Implement a trait behavior */
    implement(traitBehavior, impl) {
        this._$.implement(traitBehavior._$, impl);
        return this;
    }
    /** Add external classes */
    externalClasses(list) {
        this._$.externalClasses(list);
        return this;
    }
    export(f) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this._$export = f;
        return this;
    }
    /** Use another behavior */
    behavior(behavior) {
        this._$parents.push(behavior);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this._$ = this._$.behavior(behavior._$);
        if (behavior._$chainingFilter) {
            return behavior._$chainingFilter(this);
        }
        return this;
    }
    data(gen) {
        this._$.data(gen);
        return this;
    }
    staticData(data) {
        this._$.staticData(data);
        return this;
    }
    property(name, def) {
        this._$.property(name, def);
        return this;
    }
    /**
     * Add some public methods
     *
     * The public method can be used as an event handler, and can be visited in component instance.
     */
    methods(funcs) {
        this._$.methods(funcs);
        return this;
    }
    observer(paths, func, once = false) {
        this._$.observer(paths, func, once);
        return this;
    }
    /**
     * Add a lifetime callback
     */
    lifetime(name, func, once = false) {
        this._$.lifetime(name, func, once);
        return this;
    }
    /**
     * Add a page-lifetime callback
     */
    pageLifetime(name, func, once = false) {
        this._$.pageLifetime(name, func, once);
        return this;
    }
    /**
     * Add a relation
     */
    relation(name, rel) {
        const target = rel.target instanceof Behavior ||
            rel.target instanceof ComponentType ||
            rel.target instanceof TraitBehavior
            ? rel.target._$
            : rel.target;
        this._$.relation(name, {
            target,
            type: rel.type,
            linked: rel.linked,
            linkChanged: rel.linkChanged,
            unlinked: rel.unlinked,
            linkFailed: rel.linkFailed,
        });
        return this;
    }
    init(func) {
        this._$.init(function ({ data, setData, implement, relation, observer, lifetime, pageLifetime, method, listener, }) {
            const relationInit = ((rel) => {
                const target = rel.target instanceof Behavior ||
                    rel.target instanceof ComponentType ||
                    rel.target instanceof TraitBehavior
                    ? rel.target._$
                    : rel.target;
                return relation({
                    target,
                    type: rel.type,
                    linked: rel.linked,
                    linkChanged: rel.linkChanged,
                    unlinked: rel.unlinked,
                    linkFailed: rel.linkFailed,
                });
            });
            const implementInit = ((traitBehavior, impl) => {
                implement(traitBehavior._$, impl);
            });
            const methodCaller = this;
            const exported = func.call(methodCaller, {
                self: methodCaller,
                data,
                setData: (newData, callback) => {
                    setData(newData);
                    if (callback) {
                        glassEasel.triggerRender(methodCaller._$, () => {
                            callback();
                        });
                    }
                },
                implement: implementInit,
                relation: relationInit,
                observer,
                lifetime,
                pageLifetime,
                method,
                listener,
            });
            if (exported) {
                const exportedKeys = Object.keys(exported);
                for (let j = 0; j < exportedKeys.length; j += 1) {
                    const exportedKey = exportedKeys[j];
                    const exportItem = exported[exportedKey];
                    if (glassEasel.Component.isTaggedMethod(exportItem)) {
                        ;
                        methodCaller[exportedKey] = exportItem;
                    }
                }
            }
            return exported;
        });
        return this;
    }
    definition(def) {
        var _a;
        (_a = def.behaviors) === null || _a === void 0 ? void 0 : _a.forEach((beh) => { var _a; return (_a = beh._$boundDefinitionFilter) === null || _a === void 0 ? void 0 : _a.call(beh, def); });
        const inner = this._$;
        const { behaviors, properties: rawProperties, data: rawData, observers: rawObservers, methods, created, attached, ready, moved, detached, lifetimes: rawLifetimes, pageLifetimes: rawPageLifetimes, relations: rawRelations, externalClasses, export: exports, } = def;
        behaviors === null || behaviors === void 0 ? void 0 : behaviors.forEach((beh) => {
            this._$parents.push(beh);
            inner.behavior(beh._$);
        });
        if (rawData !== undefined) {
            if (typeof rawData === 'function') {
                inner.data(rawData);
            }
            else {
                inner.staticData(rawData);
            }
        }
        if (rawProperties !== undefined) {
            const keys = Object.keys(rawProperties);
            for (let i = 0; i < keys.length; i += 1) {
                const name = keys[i];
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                inner.property(name, rawProperties[name]);
            }
        }
        if (rawObservers !== undefined) {
            if (Array.isArray(rawObservers)) {
                for (let i = 0; i < rawObservers.length; i += 1) {
                    const { fields, observer } = rawObservers[i];
                    inner.observer(fields || '**', observer);
                }
            }
            else {
                const keys = Object.keys(rawObservers);
                for (let i = 0; i < keys.length; i += 1) {
                    const fields = keys[i];
                    const observer = rawObservers[fields];
                    inner.observer(fields, observer);
                }
            }
        }
        if (methods)
            inner.methods(methods);
        if (created && (rawLifetimes === null || rawLifetimes === void 0 ? void 0 : rawLifetimes.created) === undefined)
            inner.lifetime('created', created, true);
        if (attached && (rawLifetimes === null || rawLifetimes === void 0 ? void 0 : rawLifetimes.attached) === undefined)
            inner.lifetime('attached', attached, true);
        if (ready && (rawLifetimes === null || rawLifetimes === void 0 ? void 0 : rawLifetimes.ready) === undefined)
            inner.lifetime('ready', ready, true);
        if (moved && (rawLifetimes === null || rawLifetimes === void 0 ? void 0 : rawLifetimes.moved) === undefined)
            inner.lifetime('moved', moved, true);
        if (detached && (rawLifetimes === null || rawLifetimes === void 0 ? void 0 : rawLifetimes.detached) === undefined)
            inner.lifetime('detached', detached, true);
        if (rawLifetimes) {
            const keys = Object.keys(rawLifetimes);
            for (let i = 0; i < keys.length; i += 1) {
                const name = keys[i];
                const func = rawLifetimes[name];
                inner.lifetime(name, func, true);
            }
        }
        if (rawPageLifetimes) {
            const keys = Object.keys(rawPageLifetimes);
            for (let i = 0; i < keys.length; i += 1) {
                const name = keys[i];
                const func = rawPageLifetimes[name];
                inner.pageLifetime(name, func, true);
            }
        }
        if (rawRelations) {
            const keys = Object.keys(rawRelations);
            for (let i = 0; i < keys.length; i += 1) {
                const name = keys[i];
                const rel = rawRelations[name];
                const target = rel.target instanceof Behavior ||
                    rel.target instanceof ComponentType ||
                    rel.target instanceof TraitBehavior
                    ? rel.target._$
                    : rel.target;
                inner.relation(name, {
                    target,
                    type: rel.type,
                    linked: rel.linked,
                    linkChanged: rel.linkChanged,
                    unlinked: rel.unlinked,
                    linkFailed: rel.linkFailed,
                });
            }
        }
        if (externalClasses)
            inner.externalClasses(externalClasses);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        if (exports)
            this._$export = exports;
        return this;
    }
}
//# sourceMappingURL=base_behavior_builder.js.map